import { NextPage } from "next";
import { useIntl } from "react-intl";
import { PageProps } from "@/pages/_app";
import Layout from "@/components/layout/Layout";
import { SEO } from "@/interfaces/seo";
import HeroBlock, {
  HeroBlockProps,
} from "@/components/common/heroBlock/HeroBlock";
import { Article, ArticleProps } from "@/components/common/article/Article";
import {
  DescriptiveBlockList,
  DescriptiveBlockListProps,
} from "@/components/common/descriptiveBlockList/DescriptiveBlockList";
import { Section } from "@/components/common/section/Section";
import { FAQProps } from "@/utils/formatCategoryPage";
import { SEOWithoutTemplate } from "@/interfaces/seoWithoutTemplate";

import { Feature, isFeatureEnabled } from "@/utils/isFeatureEnabled";
import ReviewsBlock2 from "@/components/common/reviewsBlockV1/ReviewsBlock";
import ReviewsBlock1, {
  RentACarReview,
} from "@/components/common/reviewsBlock/ReviewsBlock";
import { StructuredReviewProps } from "@/components/common/structuredData/StructuredData.interface";
import {
  ArticleWithPicture,
  ArticleWithPictureProps,
} from "@/components/common/articleWithPicture/ArticleWithPicture";
import { FAQ } from "../vehicle/categoryPage/faq/FAQ";
import styles from "./JoinUsPage.module.scss";
import Services, { ServicesBlock } from "../home/services/Services";

const ReviewsBlock = ({
  name,
  agency,
  rentACarReview,
  reviewsConst,
  image,
}: StructuredReviewProps) => {
  if (!isFeatureEnabled(Feature.NEW_REVIEWS_BLOCK)) {
    return (
      <ReviewsBlock1
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  } else {
    return (
      <ReviewsBlock2
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  }
};

export type JoinUsContent = {
  heroBlock: HeroBlockProps;
  breadcrumbLabel: string;
  presentation: ArticleProps | undefined;
  leftArticleWithPicture: ArticleWithPictureProps[] | undefined;
  middleArticle: ArticleProps[] | undefined;
  servicesBlock: ServicesBlock[] | undefined;
  rightArticleWithPicture: ArticleWithPictureProps[] | undefined;
  descriptiveBlockList: DescriptiveBlockListProps | undefined;
  faq?: FAQProps;
  rentACarReview?: RentACarReview;
  seoWithoutTemplate: SEOWithoutTemplate;
  seo: SEO;
};
type JoinUsProps = PageProps &
  JoinUsContent & {
    defaultCanonicalRoute: string;
  };

export const JoinUs: NextPage<JoinUsProps> = ({
  layout,
  reviewsConst,
  heroBlock,
  breadcrumbLabel,
  presentation,

  leftArticleWithPicture,
  middleArticle,
  servicesBlock,
  rightArticleWithPicture,
  descriptiveBlockList,
  faq,
  rentACarReview,
  seoWithoutTemplate,
  seo,
  defaultCanonicalRoute,
}: JoinUsProps) => {
  const intl = useIntl();

  return (
    <>
      <Layout
        layout={layout}
        defaultCanonicalRoute={defaultCanonicalRoute}
        pageMeta={seo}
        title={seoWithoutTemplate.title}
        description={seoWithoutTemplate.description}
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: breadcrumbLabel,
            url: defaultCanonicalRoute,
          },
        ]}
      >
        <HeroBlock {...heroBlock} hideSearchBlock={true} />
        <Section>
          {presentation && <Article {...presentation} />}
          {leftArticleWithPicture?.map((article, index) => (
            <Section key={`left-${index}`}>
              <ArticleWithPicture {...article} />
            </Section>
          ))}

          {middleArticle?.map((article, index) => (
            <Section key={`middle-${index}`}>
              <Article {...article} containerStyle={styles.article} />
            </Section>
          ))}
          {servicesBlock?.map((services, index) => (
            <Section key={`services-${index}`}>
              <Services {...services} />
            </Section>
          ))}

          {rightArticleWithPicture?.map((article, index) => (
            <Section key={`right-${index}`}>
              <ArticleWithPicture {...article} />
            </Section>
          ))}
          {descriptiveBlockList && (
            <DescriptiveBlockList {...descriptiveBlockList} />
          )}
        </Section>
        {faq?.questionsAndAnswers && (
          <FAQ questionsAndAnswers={faq.questionsAndAnswers} />
        )}
        {rentACarReview?.clientReviews &&
          rentACarReview.clientReviews.length && (
            <ReviewsBlock
              rentACarReview={rentACarReview}
              reviewsConst={reviewsConst ?? undefined}
            />
          )}
      </Layout>
    </>
  );
};
